import {
  openDB,
  type DBSchema,
  type IDBPDatabase,
  type IDBPObjectStore,
  type IDBPTransaction,
  type IndexKey,
  type IndexNames,
  type OpenDBCallbacks,
  type StoreKey,
  type StoreNames,
  type StoreValue,
} from "idb";

type DatabaseOptions<Schema extends DBSchema | unknown> = {
  name: string;
  version: number;
  upgrade: OpenDBCallbacks<Schema>["upgrade"];
};

/** Shared connection lifecycle and commit boundary; schemas belong to their services. */
export class IndexedDatabase<Schema extends DBSchema | unknown> {
  private connection?: Promise<IDBPDatabase<Schema>>;

  constructor(private readonly options: DatabaseOptions<Schema>) {}

  private getDB(): Promise<IDBPDatabase<Schema>> {
    if (!this.connection) {
      const pending = this.open(() => {
        if (this.connection === pending) {
          this.connection = undefined;
        }
      }).catch((error: unknown) => {
        if (this.connection === pending) {
          this.connection = undefined;
        }
        throw error;
      });
      this.connection = pending;
    }
    return this.connection;
  }

  private open(invalidate: () => void): Promise<IDBPDatabase<Schema>> {
    return new Promise((resolve, reject) => {
      let blocked = false;
      let database: IDBPDatabase<Schema> | undefined;
      const disconnect = () => {
        database?.close();
        invalidate();
      };
      openDB<Schema>(this.options.name, this.options.version, {
        upgrade: this.options.upgrade,
        blocked: () => {
          blocked = true;
          reject(
            new Error(
              `Database "${this.options.name}" is blocked by another browser tab.`,
            ),
          );
        },
        blocking: disconnect,
        terminated: disconnect,
      }).then((opened) => {
        if (blocked) {
          opened.close();
          return;
        }
        database = opened;
        resolve(opened);
      }, reject);
    });
  }

  /** The callback may await database operations only; network/timer waits can expire a transaction. */
  public async transaction<
    Stores extends ArrayLike<StoreNames<Schema>>,
    Mode extends "readonly" | "readwrite",
    Result,
  >(
    stores: Stores,
    mode: Mode,
    run: (
      tx: IDBPTransaction<Schema, Stores, Mode>,
    ) => Result | Promise<Result>,
  ): Promise<Result> {
    const db = await this.getDB();
    const tx = db.transaction(stores, mode);
    // Observe aborts immediately while the callback is still processing requests.
    void tx.done.catch(() => undefined);
    try {
      const result = await run(tx);
      await tx.done;
      return result;
    } catch (error) {
      try {
        tx.abort();
      } catch {
        // A transaction that already completed/aborted cannot be aborted again.
      }
      await tx.done.catch(() => undefined);
      throw error;
    }
  }

  public get<Name extends StoreNames<Schema>>(
    store: Name,
    key: StoreKey<Schema, Name>,
  ): Promise<StoreValue<Schema, Name> | undefined> {
    return this.transaction([store], "readonly", (tx) =>
      tx.objectStore(store).get(key),
    );
  }

  public put<Name extends StoreNames<Schema>>(
    store: Name,
    value: StoreValue<Schema, Name>,
    key?: StoreKey<Schema, Name>,
  ): Promise<StoreValue<Schema, Name>> {
    // Own mutable inputs before waiting for a connection or transaction.
    const owned = structuredClone(value);
    const ownedKey = structuredClone(key);
    return this.transaction([store], "readwrite", async (tx) => {
      await tx.objectStore(store).put(owned, ownedKey);
      return owned;
    });
  }

  public delete<Name extends StoreNames<Schema>>(
    store: Name,
    key: StoreKey<Schema, Name>,
  ): Promise<void> {
    return this.transaction([store], "readwrite", (tx) =>
      tx.objectStore(store).delete(key),
    );
  }

  public getAllFromIndex<
    Name extends StoreNames<Schema>,
    Index extends IndexNames<Schema, Name>,
  >(
    store: Name,
    index: Index,
    key: IndexKey<Schema, Name, Index>,
  ): Promise<Array<StoreValue<Schema, Name>>> {
    return this.transaction([store], "readonly", (tx) =>
      tx.objectStore(store).index(index).getAll(key),
    );
  }

  public async close(): Promise<void> {
    const connection = this.connection;
    this.connection = undefined;
    const db = await connection;
    db?.close();
  }
}

/** Injectable service boundary; tests can supply a storage implementation. */
export type DatabaseStorage<Schema extends DBSchema | unknown> = Pick<
  IndexedDatabase<Schema>,
  "get" | "put" | "delete" | "getAllFromIndex" | "transaction"
>;

/** Queue a batch together instead of awaiting each write in turn. */
export async function putMany<
  Schema extends DBSchema | unknown,
  Stores extends ArrayLike<StoreNames<Schema>>,
  Name extends StoreNames<Schema>,
>(
  store: IDBPObjectStore<Schema, Stores, Name, "readwrite">,
  values: ReadonlyArray<StoreValue<Schema, Name>>,
): Promise<void> {
  // Async callbacks also capture synchronous put failures without leaving earlier requests unobserved.
  await Promise.all(values.map(async (value) => store.put(value)));
}
